import {
  Accordion,
  Alert,
  Anchor,
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  Drawer,
  List,
  Modal,
  MultiSelect,
  NumberInput,
  Pill,
  Radio,
  Select,
  Table,
  Tabs,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { Notifications } from '@mantine/notifications';

import anchorStyles from './mantineAnchor.module.scss';
import checkboxStyles from './mantineCheckbox.module.scss';
import datePickerInputStyles from './mantineDatePickerInput.module.scss';
import drawerStyles from './mantineDrawer.module.scss';
import listStyles from './mantineList.module.scss';
import modalStyles from './mantineModal.module.scss';
import numberInputStyles from './mantineNumberInput.module.scss';
import pillStyles from './mantinePill.module.scss';
import selectStyles from './mantineSelect.module.scss';
import tableStyles from './mantineTable.module.scss';
import tabStyles from './mantineTabs.module.scss';
import textInputStyles from './mantineTextInput.module.scss';
import textareaStyles from './mantineTextarea.module.scss';
import tootlipStyles from './mantineTooltip.module.scss';

export default {
  focusRing: 'never',
  primaryColor: 'lionstep-blue',
  primaryShade: 5,
  colors: {
    'lionstep-blue': [
      '#F6F8FD',
      '#DDE5F9',
      '#BACAF2',
      '#98B0EC',
      '#7695E5',
      '#537BDF',
      '#4262B2',
      '#213057',
      '#192543',
      '#16213C',
    ],
    'lionstep-gray': [
      '#FAFAF9',
      '#F6F5F3',
      '#ECEBE7',
      '#E3E1DB',
      '#D9D7CE',
      '#CFCDC2',
      '#A6A39B',
      '#7C7A74',
      '#686661',
      '#5F5D59',
    ],
  },
  components: {
    Accordion: Accordion.extend({
      defaultProps: {
        variant: 'separated',
      },
    }),
    Alert: Alert.extend({
      defaultProps: {
        radius: 'md',
      },
    }),
    Anchor: Anchor.extend({
      defaultProps: {
        fz: 'sm',
        underline: 'never',
      },
      classNames: {
        root: anchorStyles.root,
      },
    }),
    Autocomplete: Autocomplete.extend({
      defaultProps: {
        fz: 'sm',
        pb: 'md',
        pos: 'relative',
        radius: 'md',
        size: 'md',
        classNames: {
          input: selectStyles.input,
          dropdown: selectStyles.dropdown,
          option: selectStyles.option,
        },
        errorProps: {
          bottom: 0,
          fz: 'xs',
          left: 0,
          pos: 'absolute',
        },
        labelProps: {
          fw: '400',
          fz: 'sm',
        },
      },
      styles: {
        wrapper: {
          marginBottom: 0,
        },
      },
    }),
    Badge: Badge.extend({
      defaultProps: {
        fz: 'xs',
        fw: '500',
        tt: 'unset',
        lh: 'sm',
        px: '15',
        py: '3',
        h: 'auto',
      },
      vars: (theme, props) => {
        switch (props.variant) {
          case 'info':
            return {
              root: {
                '--badge-bd': `1px solid ${theme.colors['lionstep-blue'][6]}`,
                '--badge-bg': theme.colors['lionstep-blue'][6],
                '--badge-color': theme.white,
              },
            };
          case 'success':
            return {
              root: {
                '--badge-bd': '1px solid #00977c',
                '--badge-bg': '#00977c',
                '--badge-color': theme.white,
              },
            };
          case 'danger':
            return {
              root: {
                '--badge-bd': '1px solid #ea4858',
                '--badge-bg': '#ea4858',
                '--badge-color': theme.white,
              },
            };
          case 'warning':
            return {
              root: {
                '--badge-bd': '1px solid #bd8510',
                '--badge-bg': '#bd8510',
                '--badge-color': theme.white,
              },
            };
          case 'disabled':
            return {
              root: {
                '--badge-bd': `1px solid ${theme.colors['lionstep-blue'][6]}`,
                '--badge-bg': theme.colors['lionstep-blue'][6],
                '--badge-color': theme.white,
              },
            };
          case 'ghost-info':
            return {
              root: {
                '--badge-bd': `1px solid ${theme.colors['lionstep-blue'][6]}`,
                '--badge-bg': 'transparent',
                '--badge-color': theme.colors['lionstep-blue'][6],
              },
            };
          case 'ghost-success':
            return {
              root: {
                '--badge-bd': '1px solid #00977c',
                '--badge-bg': 'transparent',
                '--badge-color': '#00977c',
              },
            };
          case 'ghost-danger':
            return {
              root: {
                '--badge-bd': '1px solid #ea4858',
                '--badge-bg': 'transparent',
                '--badge-color': '#ea4858',
              },
            };
          case 'ghost-warning':
            return {
              root: {
                '--badge-bd': '1px solid #bd8510',
                '--badge-bg': 'transparent',
                '--badge-color': '#bd8510',
              },
            };
          case 'ghost-disabled':
            return {
              root: {
                '--badge-bd': `1px solid ${theme.colors['lionstep-blue'][6]}`,
                '--badge-bg': 'transparent',
                '--badge-color': theme.colors['lionstep-blue'][6],
              },
            };
          default:
            return {};
        }
      },
    }),
    Button: Button.extend({
      defaultProps: {
        radius: 'xl',
        size: 'md',
        fz: 'sm',
        fw: 400,
      },
    }),
    Checkbox: Checkbox.extend({
      defaultProps: {
        size: 'md',
      },
      classNames: {
        label: checkboxStyles.label,
      },
    }),
    DatePickerInput: DatePickerInput.extend({
      defaultProps: {
        fz: 'sm',
        pb: 'md',
        pos: 'relative',
        radius: 'md',
        size: 'md',
        valueFormat: 'DD/MM/YYYY',
        errorProps: {
          bottom: 0,
          fz: 'xs',
          left: 0,
          pos: 'absolute',
        },
        labelProps: {
          fw: '400',
          fz: 'sm',
        },

        popoverProps: {
          fz: 'sm',
          radius: 'md',
          size: 'md',
          classNames: {
            dropdown: datePickerInputStyles.dropdown,
          },
        },
      },
      classNames: {
        input: datePickerInputStyles.input,
        calendarHeaderLevel: datePickerInputStyles.calendarHeaderLevel,
        weekday: datePickerInputStyles.weekday,
        day: datePickerInputStyles.day,
        levelsGroup: datePickerInputStyles.levelsGroup,
      },
      styles: {
        wrapper: {
          marginBottom: 0,
        },
      },
    }),
    Drawer: Drawer.extend({
      classNames: {
        header: drawerStyles.header,
      },
    }),
    List: List.extend({
      classNames: {
        root: listStyles.listRoot,
        itemLabel: listStyles.listItemLabel,
        itemWrapper: listStyles.listItemWrapper,
      },
      defaultProps: {
        size: 'sm',
        withPadding: true,
      },
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        fz: 'sm',
        pb: 'md',
        pos: 'relative',
        radius: 'md',
        size: 'md',
        checkIconPosition: 'right',
        classNames: {
          input: selectStyles.input,
          dropdown: selectStyles.dropdown,
          option: selectStyles.option,
        },
        errorProps: {
          bottom: 0,
          fz: 'xs',
          left: 0,
          pos: 'absolute',
        },
        labelProps: {
          fw: '400',
          fz: 'sm',
        },
      },
      styles: {
        wrapper: {
          marginBottom: 0,
        },
      },
    }),
    Modal: Modal.extend({
      defaultProps: {
        radius: 'lg',
        centered: true,
      },
      classNames: {
        content: modalStyles.content,
        header: modalStyles.header,
        title: modalStyles.title,
      },
    }),
    Notifications: Notifications.extend({
      defaultProps: {
        position: 'top-right',
        maw: 400,
      },
    }),
    NumberInput: NumberInput.extend({
      defaultProps: {
        fz: 'sm',
        pb: 'md',
        pos: 'relative',
        radius: 'md',
        size: 'md',
        errorProps: {
          bottom: 0,
          fz: 'xs',
          left: 0,
          pos: 'absolute',
        },
        labelProps: {
          fw: '400',
          fz: 'sm',
        },
      },
      classNames: {
        input: numberInputStyles.input,
      },
      styles: {
        wrapper: {
          marginBottom: 0,
        },
      },
    }),
    Pill: Pill.extend({
      classNames: {
        root: pillStyles.root,
        label: pillStyles.label,
      },
    }),
    Radio: Radio.extend({
      defaultProps: {
        fz: 'sm',
        pb: 'md',
        pos: 'relative',
        size: 'sm',
        errorProps: {
          bottom: 0,
          fz: 'xs',
          left: 0,
          pos: 'absolute',
        },
        labelProps: {
          fw: '400',
          fz: 'sm',
        },
      },
    }),
    Select: Select.extend({
      defaultProps: {
        allowDeselect: false,
        fz: 'sm',
        pb: 'md',
        pos: 'relative',
        radius: 'md',
        size: 'md',
        checkIconPosition: 'right',
        classNames: {
          input: selectStyles.input,
          dropdown: selectStyles.dropdown,
          option: selectStyles.option,
        },
        errorProps: {
          bottom: 0,
          fz: 'xs',
          left: 0,
          pos: 'absolute',
        },
        labelProps: {
          fw: '400',
          fz: 'sm',
        },
      },
      styles: {
        wrapper: {
          marginBottom: 0,
        },
      },
    }),

    Table: Table.extend({
      classNames: {
        table: tableStyles.table,
        thead: tableStyles.thead,
        th: tableStyles.th,
        td: tableStyles.td,
        tr: tableStyles.tr,
      },
    }),
    Tabs: Tabs.extend({
      defaultProps: {
        radius: 0,
        classNames: {
          list: tabStyles.list,
          tab: tabStyles.tab,
        },
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        fz: 'sm',
        pb: 'md',
        pos: 'relative',
        radius: 'md',
        size: 'md',
        classNames: {
          input: textInputStyles.input,
        },
        errorProps: {
          bottom: 0,
          fz: 'xs',
          left: 0,
          pos: 'absolute',
        },
        labelProps: {
          fw: '400',
          fz: 'sm',
        },
      },
      styles: {
        wrapper: {
          marginBottom: 0,
        },
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        fz: 'sm',
        pb: 'md',
        pos: 'relative',
        radius: 'md',
        size: 'md',
        classNames: {
          input: textareaStyles.input,
        },
        errorProps: {
          bottom: 0,
          fz: 'xs',
          left: 0,
          pos: 'absolute',
        },
        labelProps: {
          fw: '400',
          fz: 'sm',
        },
      },
      styles: {
        wrapper: {
          marginBottom: 0,
        },
      },
    }),

    Tooltip: Tooltip.extend({
      defaultProps: {
        withArrow: true,
        classNames: {
          tooltip: tootlipStyles.tooltip,
        },
      },
    }),
  },
};
